import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { DismissibleErrorBanner } from '../../components/common/DismissibleErrorBanner';
import { apiGet, apiPost, apiPut, apiDelete } from '../../services/api/client';
import { getAdminUsers, getEmployeeTypes } from '../../services/api/admin';
import { formatDisplayDate } from '../../utils/format';
import { DateField } from '../../components/ui/DateField';
import { LEGACY_CZ_NUMBER_LOCALE, numberFormatter } from '../../utils/localeFormat';

interface ExtraCost {
  id: number;
  validFrom: string;
  validTo: string | null;
  placeCosts: number;
  serviceCosts: number;
  hours: number;
  avgUsers: number;
  hourlyCosts: string | number;
}

function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return numberFormatter({
    style: 'currency',
    currency: 'CZK',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }, LEGACY_CZ_NUMBER_LOCALE).format(num);
}

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return numberFormatter({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }, LEGACY_CZ_NUMBER_LOCALE).format(value);
}

export default function ExtraCostPage() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<ExtraCost | null>(null);
  const [showAll, setShowAll] = useState(false);
  // F4-006 — Single page-level banner for create/update/delete mutation
  // rejections. Without onError + a visible banner the modal stayed open
  // with no feedback on a 4xx/5xx response.
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: costs, isLoading, error } = useQuery<ExtraCost[]>({
    queryKey: ['extraCosts'],
    queryFn: () =>
      apiGet<{ data: ExtraCost[] }>('/admin/extra-costs').then((res) => res.data),
  });

  // Debug: log any errors
  if (error) {
    console.error('Failed to fetch extra costs:', error);
  }

  const createMutation = useMutation({
    mutationFn: (data: Partial<ExtraCost>) => apiPost('/admin/extra-costs', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['extraCosts'] });
      setActionError(null);
      setIsModalOpen(false);
    },
    onError: (err: Error) => setActionError(err.message || 'Failed to create extra cost'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: ExtraCost) => apiPut(`/admin/extra-costs/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['extraCosts'] });
      setActionError(null);
      setIsModalOpen(false);
      setEditingCost(null);
    },
    onError: (err: Error) => setActionError(err.message || 'Failed to update extra cost'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDelete(`/admin/extra-costs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['extraCosts'] });
    },
    onError: (err: Error) => setActionError(err.message || 'Failed to delete extra cost'),
  });

  const handleEdit = (cost: ExtraCost) => {
    setEditingCost(cost);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this extra cost record?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-red-600 font-medium">Failed to load extra costs</p>
        <p className="text-red-500 text-sm mt-1">{(error as Error).message}</p>
      </div>
    );
  }

  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - 12);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  const filteredCosts = showAll ? costs : costs?.filter((c) => c.validFrom >= cutoffStr);
  const hiddenCount = (costs?.length ?? 0) - (filteredCosts?.length ?? 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Extra Costs</h1>
        <Button onClick={() => setIsModalOpen(true)}>New</Button>
      </div>

      {/* F4-006 — Surface mutation rejections so create/update/delete
          failures don't leave the modal silently stuck open. */}
      <DismissibleErrorBanner message={actionError} onDismiss={() => setActionError(null)} />

      <p className="text-sm text-gray-500">
        Extra costs represent company-wide overhead (rent, services, etc.) that get distributed
        across hours worked. The hourly cost is calculated as: (Place costs + Service costs) /
        Average users / Hours per month.
      </p>

      {!costs || costs.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-200 p-12 text-center">
          <p className="text-gray-500">No extra cost records found.</p>
          <Button className="mt-4" onClick={() => setIsModalOpen(true)}>
            Add First Record
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Valid from
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Valid to
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Place costs
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Service costs
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Hours
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Avg Users
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Hourly costs per user
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCosts?.map((cost) => (
                <tr key={cost.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDisplayDate(cost.validFrom)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDisplayDate(cost.validTo)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(cost.placeCosts)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(cost.serviceCosts)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatNumber(cost.hours)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {cost.avgUsers}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                    {formatCurrency(cost.hourlyCosts)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(cost)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(cost.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {hiddenCount > 0 && !showAll && (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-center">
              <button
                onClick={() => setShowAll(true)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Show all ({hiddenCount} older record{hiddenCount !== 1 ? 's' : ''} hidden)
              </button>
            </div>
          )}
          {showAll && hiddenCount > 0 && (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-center">
              <button
                onClick={() => setShowAll(false)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Show last 12 months only
              </button>
            </div>
          )}
        </div>
      )}

      {isModalOpen && (
        <ExtraCostModal
          cost={editingCost}
          onClose={() => {
            setIsModalOpen(false);
            setEditingCost(null);
          }}
          onSave={(data) => {
            if (editingCost) {
              updateMutation.mutate({ ...editingCost, ...data } as ExtraCost);
            } else {
              createMutation.mutate(data);
            }
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}

function useActiveEmployeeCount() {
  const { data: users } = useQuery({ queryKey: ['adminUsers', false], queryFn: () => getAdminUsers() });
  const { data: types } = useQuery({ queryKey: ['employee-types'], queryFn: getEmployeeTypes });

  if (!users || !types) return undefined;

  const eligibleTypeIds = new Set(
    types
      .filter((t) => t.name === 'Freelance' || t.name === 'Employee')
      .map((t) => t.id)
  );

  const today = new Date().toISOString().slice(0, 10);
  return users.filter(
    (u) =>
      !u.deletedAt &&
      u.employeeTypeId !== null &&
      eligibleTypeIds.has(u.employeeTypeId) &&
      (!u.endDate || u.endDate >= today)
  ).length;
}

function ExtraCostModal({
  cost,
  onClose,
  onSave,
  isLoading,
}: {
  cost: ExtraCost | null;
  onClose: () => void;
  onSave: (data: Partial<ExtraCost>) => void;
  isLoading: boolean;
}) {
  const activeCount = useActiveEmployeeCount();

  const [formData, setFormData] = useState({
    validFrom: cost?.validFrom || '',
    validTo: cost?.validTo || '',
    placeCosts: cost?.placeCosts?.toString() || '',
    serviceCosts: cost?.serviceCosts?.toString() || '',
    hours: cost?.hours?.toString() || '160',
    avgUsers: cost?.avgUsers?.toString() || '',
  });

  // Pre-fill avgUsers with active employee count for new records
  useEffect(() => {
    if (!cost && activeCount !== undefined && !formData.avgUsers) {
      setFormData((prev) => ({ ...prev, avgUsers: String(activeCount) }));
    }
  }, [activeCount, cost, formData.avgUsers]);

  // Calculate preview of hourly costs
  const previewHourlyCosts = () => {
    const place = parseFloat(formData.placeCosts) || 0;
    const service = parseFloat(formData.serviceCosts) || 0;
    const hours = parseFloat(formData.hours) || 160;
    const users = parseFloat(formData.avgUsers) || 1;
    if (users === 0 || hours === 0) return 0;
    return (place + service) / users / hours;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Partial<ExtraCost> = {
      validFrom: formData.validFrom,
      placeCosts: parseFloat(formData.placeCosts) || 0,
      serviceCosts: parseFloat(formData.serviceCosts) || 0,
      hours: parseFloat(formData.hours) || 160,
      avgUsers: parseInt(formData.avgUsers, 10) || 1,
    };
    if (formData.validTo) {
      payload.validTo = formData.validTo;
    }
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="extra-cost-modal-title">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        <div className="relative w-full max-w-lg bg-white rounded-lg shadow-xl">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 id="extra-cost-modal-title" className="text-lg font-semibold text-gray-900">
              {cost ? 'Edit Extra Cost' : 'New Extra Cost'}
            </h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded text-2xl">
              &times;
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
                <DateField
                  value={formData.validFrom}
                  onChange={(nextDate) => setFormData({ ...formData, validFrom: nextDate })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valid To <span className="text-gray-400">(optional)</span>
                </label>
                <DateField
                  value={formData.validTo}
                  onChange={(nextDate) => setFormData({ ...formData, validTo: nextDate })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Place Costs (monthly)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.placeCosts}
                  onChange={(e) => setFormData({ ...formData, placeCosts: e.target.value })}
                  required
                  placeholder="Rent, utilities, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Costs (monthly)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.serviceCosts}
                  onChange={(e) => setFormData({ ...formData, serviceCosts: e.target.value })}
                  required
                  placeholder="Software, services, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hours per Month
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  value={formData.hours}
                  onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                  required
                  placeholder="160"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Average Users
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.avgUsers}
                  onChange={(e) => setFormData({ ...formData, avgUsers: e.target.value })}
                  required
                  placeholder="Number of employees"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  Calculated hourly cost per user:
                </span>
                <span className="text-lg font-semibold text-primary-600">
                  {formatCurrency(previewHourlyCosts())}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                = (Place costs + Service costs) / Avg users / Hours
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
